import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router';

import { AppDispatch } from 'store';
import { logout, UserState } from 'store/userSlice';

import Button from 'layout/atoms/Button';
import Container from 'layout/atoms/Container';
import Dropdown from 'layout/atoms/Dropdown';
import Logo from 'layout/atoms/Logo';
import Separator from 'layout/atoms/Separator';
import LanguageToggle from 'layout/atoms/LanguageToggle';
import SoundToggle from 'layout/atoms/SoundToggle';
import ThemeToggle from 'layout/atoms/ThemeToggle';
import Tooltip from 'layout/atoms/Tooltip';

import { ReactComponent as FireIcon } from 'assets/icons/fire.svg';
import { ReactComponent as PersonIcon } from 'assets/icons/person.svg';
import { ReactComponent as PowerIcon } from 'assets/icons/power.svg';
import { ReactComponent as PricetagIcon } from 'assets/icons/pricetag.svg';
import { ReactComponent as RepeatIcon } from 'assets/icons/repeat.svg';
import FeatureFlag from 'src/feature-flag/FeatureFlag';

import styles from './Topbar.module.scss';

export const Topbar = (props: { active: boolean }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const [scrollOnTop, setScrollOnTop] = useState<boolean>(true);
    const userState: UserState = useSelector((state: any) => state.user);
    const location = useLocation();
    const navRef = useRef<HTMLElement>(null);
    const [indicatorStyle, setIndicatorStyle] = useState({ x: 0, width: 0 });

    const signOut = async () => {
        localStorage.removeItem('hustleToken');
        dispatch(logout());
    };

    const toggleMenu = () => {
        let distanceFromTop = window.pageYOffset;

        return function () {
            let newDistanceFromTop = window.pageYOffset;
            if (
                newDistanceFromTop < 50 ||
                distanceFromTop >= newDistanceFromTop
            ) {
                setShowMenu(true);
            } else {
                setShowMenu(false);
            }
            if (newDistanceFromTop < 50) {
                setScrollOnTop(true);
            } else {
                setScrollOnTop(false);
            }
            distanceFromTop = newDistanceFromTop;
        };
    };

    useEffect(() => {
        setTimeout(() => setShowMenu(true), 500);
        window.addEventListener('scroll', toggleMenu());
        return window.removeEventListener('scroll', toggleMenu());
    }, []);

    const isWorkspacePage =
        location.pathname.startsWith('/plan') ||
        location.pathname.startsWith('/execute');

    useEffect(() => {
        const activeLink = navRef.current?.querySelector(
            `.${styles.active}`
        ) as HTMLElement;
        if (activeLink) {
            const width = activeLink.offsetWidth * 0.5;
            setIndicatorStyle({
                x: activeLink.offsetLeft + (activeLink.offsetWidth - width) / 2,
                width,
            });
        }
    }, [location.pathname]);

    return (
        <div
            className={`${styles.wrap} ${
                props.active && showMenu && styles.showMenu
            } ${scrollOnTop && styles.scrollOnTop}`}
        >
            <Container>
                <div className={styles.content}>
                    <Logo />
                    <nav className={styles.nav} ref={navRef}>
                        <NavLink
                            to="/plan"
                            className={({ isActive }: { isActive: any }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active}`
                                    : styles.navItem
                            }
                        >
                            {t('NAV.PLAN')}
                        </NavLink>
                        <NavLink
                            to="/execute"
                            className={({ isActive }: { isActive: any }) =>
                                isActive
                                    ? `${styles.navItem} ${styles.active}`
                                    : styles.navItem
                            }
                        >
                            {t('NAV.EXECUTE')}
                        </NavLink>
                        <div
                            className={styles.indicator}
                            style={{
                                width: indicatorStyle.width,
                                transform: `translate3d(${indicatorStyle.x}px, 0, 0)`,
                                opacity: isWorkspacePage ? 1 : 0,
                            }}
                        />
                    </nav>
                    <aside className={styles.aside}>
                        <Tooltip content={t('TOPBAR.AUTOMATION')}>
                            <Button
                                size="m"
                                theme="blank"
                                shape="square"
                                to="/automation"
                            >
                                <RepeatIcon />
                            </Button>
                        </Tooltip>
                        {userState.data && (
                            <Dropdown
                                position="bottomRight"
                                trigger={
                                    <Button
                                        size="m"
                                        theme="blank"
                                        shape="square"
                                    >
                                        <PersonIcon />
                                    </Button>
                                }
                            >
                                <div className={styles.userInfo}>
                                    <p>{userState.data.userEmail}</p>
                                </div>
                                <Separator />
                                <FeatureFlag feature="subscription">
                                    <Button
                                        size="m"
                                        theme="blank"
                                        align="left"
                                        to="/subscription"
                                    >
                                        <PricetagIcon />{' '}
                                        <span>{t('TOPBAR.SUBSCRIPTION')}</span>
                                    </Button>
                                </FeatureFlag>
                                <Button
                                    size="m"
                                    theme="blank"
                                    align="left"
                                    to="/feedback-ideas"
                                >
                                    <FireIcon /> <span>{t('TOPBAR.FEEDBACK_IDEAS')}</span>
                                </Button>
                                <Separator />
                                <ThemeToggle />
                                <SoundToggle />
                                <LanguageToggle />
                                <Separator />
                                <Button
                                    size="m"
                                    theme="blank"
                                    align="left"
                                    onClick={signOut}
                                >
                                    <PowerIcon /> <span>{t('TOPBAR.LOGOUT')}</span>
                                </Button>
                            </Dropdown>
                        )}
                    </aside>
                </div>
            </Container>
        </div>
    );
};
